import React from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import { PartnerMember, PartnerResource } from "../../api/partners";
import { downloadMembersCsv } from "../../utils/memberCsv";
import { useDialogState } from "../../hooks/useDialogState";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useSnackbar } from "../../hooks/useSnackbar";
import { ConfirmActionDialog } from "../common/ConfirmActionDialog";
import { InfinitePaginationTable } from "../common/InfinitePaginationTable";
import { BulkImportDialog } from "./BulkImportDialog";

type MemberRow = PartnerMember & { id: string };

interface PartnerMembersDialogProps {
  open: boolean;
  partnerId: string;
  partnerName: string;
  onClose: () => void;
}

export const PartnerMembersDialog: React.FC<PartnerMembersDialogProps> = ({
  open,
  partnerId,
  partnerName,
  onClose,
}) => {
  const { showSnackbar, SnackbarNode } = useSnackbar();
  const bulkDialog = useDialogState();
  const removeConfirm = useDialogState<PartnerMember>();
  const isMobile = useIsMobile();

  const [members, setMembers] = React.useState<PartnerMember[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [email, setEmail] = React.useState("");
  const [displayName, setDisplayName] = React.useState("");
  const [adding, setAdding] = React.useState(false);
  const [removingId, setRemovingId] = React.useState<string>();

  const loadMembers = React.useCallback(async () => {
    setLoading(true);
    try {
      setMembers(await PartnerResource.listMembers(partnerId));
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : "Failed to load members.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [partnerId, showSnackbar]);

  React.useEffect(() => {
    if (open) {
      loadMembers();
    }
  }, [open, loadMembers]);

  const handleAdd = async () => {
    if (!email.trim() || !displayName.trim()) return;
    setAdding(true);
    try {
      const result = await PartnerResource.addMember(partnerId, {
        email: email.trim(),
        displayName: displayName.trim(),
      });
      const note = result.alreadyHadActiveSubscription
        ? " (already had active subscription — promo skipped)"
        : "";
      showSnackbar(`Member ${result.status}${note}.`);
      setEmail("");
      setDisplayName("");
      await loadMembers();
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : "Failed to add member.",
        "error"
      );
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (member: PartnerMember) => {
    setRemovingId(member.appUserId);
    try {
      await PartnerResource.removeMember(partnerId, {
        appUserId: member.appUserId,
      });
      showSnackbar(`${member.email} offboarded.`);
      setMembers((prev) =>
        prev.filter((m) => m.appUserId !== member.appUserId)
      );
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : "Failed to offboard member.",
        "error"
      );
    } finally {
      setRemovingId(undefined);
      removeConfirm.close();
    }
  };

  const handleExportCsv = React.useCallback(() => {
    downloadMembersCsv(members, partnerName, partnerName);
  }, [members, partnerName]);

  const memberRows: MemberRow[] = members.map((m) => ({
    ...m,
    id: m.appUserId,
  }));

  const memberColumns = [
    {
      id: "displayName",
      label: "Name",
      render: (member: MemberRow) => member.displayName || "—",
    },
    {
      id: "email",
      label: "Email",
    },
    {
      id: "createdAt",
      label: "Created",
      hideOnMobile: true,
      render: (member: MemberRow) =>
        member.createdAt
          ? new Date(member.createdAt).toLocaleDateString()
          : "—",
    },
    {
      id: "actions",
      label: "Actions",
      hideOnMobile: true,
      render: (member: MemberRow) => {
        const isRemoving = removingId === member.appUserId;
        return (
          <Tooltip title="Offboard from partner">
            <span>
              <IconButton
                size="small"
                color="error"
                disabled={isRemoving}
                onClick={() => removeConfirm.openWith(member)}
              >
                {isRemoving ? (
                  <CircularProgress size={16} />
                ) : (
                  <DeleteIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <span>Members — {partnerName}</span>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={handleExportCsv}
              disabled={loading || members.length === 0}
            >
              Export CSV
            </Button>
            <Button
              size="small"
              startIcon={<UploadFileIcon />}
              onClick={bulkDialog.openDialog}
            >
              Bulk Import
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Add member */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1,
              mb: 2,
              alignItems: { xs: "stretch", sm: "flex-start" },
            }}
          >
            <TextField
              size="small"
              label="Email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              size="small"
              label="Display Name"
              fullWidth
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
            <Button
              variant="contained"
              startIcon={
                adding ? <CircularProgress size={16} /> : <PersonAddIcon />
              }
              onClick={handleAdd}
              disabled={adding || !email.trim() || !displayName.trim()}
              sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
            >
              Add
            </Button>
          </Box>

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {loading ? "Loading…" : `Members (${members.length})`}
          </Typography>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : members.length === 0 ? (
            <Typography
              variant="body2"
              color="text.disabled"
              sx={{ py: 2, textAlign: "center" }}
            >
              No members yet.
            </Typography>
          ) : (
            <InfinitePaginationTable
              columns={memberColumns}
              items={memberRows}
              pageSize={10}
              onLoadNext={() => {}}
            />
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <BulkImportDialog
        open={bulkDialog.open}
        partnerId={partnerId}
        partnerName={partnerName}
        onClose={bulkDialog.close}
        onImported={loadMembers}
      />

      <ConfirmActionDialog
        open={removeConfirm.open}
        title="Offboard member?"
        message={
          <>
            <strong>
              {removeConfirm.data?.displayName || removeConfirm.data?.email}
            </strong>{" "}
            will be offboarded from <strong>{partnerName}</strong>: their paid
            access is revoked, they leave the partner's group, the partner tag
            is removed, and an offboarding email may be sent. This cannot be
            undone.
          </>
        }
        confirmLabel="Offboard member"
        loading={removingId !== undefined}
        onClose={removeConfirm.close}
        onConfirm={() => {
          if (removeConfirm.data) handleRemove(removeConfirm.data);
        }}
      />

      {SnackbarNode}
    </>
  );
};
